import { FaroSwitch } from "@components/common/faro-switch";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { neutral } from "@flat-ui";
import { Box, SvgIcon, Typography } from "@mui/material";
import { IntegrationTileError } from "@pages/project-details/project-integrations/integration-tile-error";
import { IntegrationTileSkeleton } from "@pages/project-details/project-integrations/integration-tile-skeleton";
import { INTEGRATION_TILE_WIDTH } from "@pages/project-details/project-integrations/project-integrations-constants";
import { IntegrationsService } from "@services/integrations-service/integrations-service";
import { ProjectIntegrationsMap, ServiceLegacyMap } from "@services/integrations-service/integrations-service-types";
import { INTEGRATION_INFO } from "@src/constants/integrations-constants";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { withEllipsis } from "@styles/common-styles";
import { cardProperties } from "@utils/ui-utils";
import { useCallback, useEffect, useState } from "react";

interface Props {
  /** Integration ID */
  integrationId: FaroCoreApiTypes.IntegrationId;

  /** Callback function triggered when the action button is clicked */
  onActionButtonClick(integrationId: FaroCoreApiTypes.IntegrationId): void;

  /** Integrations service instance */
  integrationsService: IntegrationsService;

  /** Callback to set the project integrations map */
  setProjectIntegrations: (projectIntegrations: ProjectIntegrationsMap) => void;

  /** Project integrations map */
  projectIntegrations?: ProjectIntegrationsMap | null;
}

/** Renders a project integration tile */
export function IntegrationTile({
  integrationId,
  onActionButtonClick,
  integrationsService,
  setProjectIntegrations,
  projectIntegrations,
}: Props): JSX.Element {
  const { handleErrorWithToast } = useErrorContext();
  const [hasCheckedDataFormat, setHasCheckedDataFormat] = useState(false);
  const [isUpdateError, setIsUpdateError] = useState(false);

  const projectName = projectIntegrations?.[integrationId]?.projectName;
  const isConnectedInXGFormat = !!projectIntegrations &&
    integrationsService.isConnectedInXGFormat(integrationId, projectIntegrations);
  const isConnectedInLegacyFormat = !!projectIntegrations &&
    integrationsService.isConnectedInLegacyFormat(integrationId, projectIntegrations);
  const isConnectionOutdated = isConnectedInLegacyFormat && !isConnectedInXGFormat;
  const isError = isUpdateError && isConnectionOutdated;

  /** Updates the project connection data from legacy to XG format */
  const updateDataFormat = useCallback(async (
    data: ServiceLegacyMap
  ) => {
    try {
      const response = await integrationsService.updateDataFormat(integrationId, data);
      setProjectIntegrations(response);
    } catch (error) {
      handleErrorWithToast({
        id: `updateDataFormat-${Date.now().toString()}`,
        title: "Failed to update the connection data format.",
        error,
      });
      setIsUpdateError(true);
    }
  }, [handleErrorWithToast, integrationId, integrationsService, setProjectIntegrations]);

  /** Checks the data format of the project connection */
  const checkDataFormat = useCallback(async () => {
    if (isConnectionOutdated) {
      await updateDataFormat(projectIntegrations);
    }

    setHasCheckedDataFormat(true);
  }, [isConnectionOutdated, projectIntegrations, updateDataFormat]);

  /** Triggers the data format check if not carried out yet and if data has been fetched  */
  useEffect(() => {
    // Only trigger the data format check once the project integrations have been fetched.
    // Fetched project integration data can either be an object or undefined, whereas null indicates "not fetched yet".
    if (!hasCheckedDataFormat && projectIntegrations !== null) {
      void checkDataFormat();
    }
  }, [checkDataFormat, hasCheckedDataFormat, projectIntegrations]);

  if (!hasCheckedDataFormat) {
    return <IntegrationTileSkeleton />;
  }

  return (
    <Box
      component="div"
      sx={{
        width: INTEGRATION_TILE_WIDTH,
        height: isError ? undefined : "200px",
        minHeight: isError ? "200px" : undefined,
        marginTop: cardProperties.marginTop,
        boxShadow: cardProperties.boxShadow,
        borderRadius: cardProperties.borderRadius,
        padding: "16px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <Box>
        <Box
          sx={{
            marginBottom: "20px",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <SvgIcon
            inheritViewBox={true}
            component={INTEGRATION_INFO[integrationId].icon}
            sx={{ width: "50px", height: "50px" }}
          />
          <Typography
            sx={{
              color: neutral[800],
              fontSize: "16px",
              lineHeight: "24px",
              fontWeight: "600",
            }}
          >
            {INTEGRATION_INFO[integrationId].displayName}
          </Typography>
        </Box>

        {isError ? (
          <IntegrationTileError />
        ) : (
          <Typography
            sx={{
              color: neutral[600],
              fontSize: "14px",
              lineHeight: "20px",
              fontWeight: "400",
            }}
          >
            {INTEGRATION_INFO[integrationId].description}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          borderTop: `1px solid ${neutral[200]}`,
          textAlign: "right",
          pt: "12px",
          display: "flex",
          alignItems: "center",
          justifyContent: projectName ? "space-between" : "flex-end",
          height: "34px",
        }}
      >
        {projectName && (
          <SphereTooltip
            component="var"
            title={projectName}
            boxProps={{
              sx: {
                ...withEllipsis,
                padding: "8px 0px",
                fontWeight: "400",
              },
            }}
          >
            <Typography
              sx={{
                fontSize: "12px",
                lineHeight: "18px",
                fontWeight: "700",
                ...withEllipsis,
              }}
            >
              Synced with
              <Box
                component="var"
                sx={{
                  ml: "4px",
                }}
              >
                {projectName}
              </Box>
            </Typography>
          </SphereTooltip>
        )}

        <FaroSwitch
          checked={isConnectedInXGFormat || isConnectedInLegacyFormat}
          onChange={() => onActionButtonClick(integrationId)}
          tooltipTextNotChecked="Enable"
          tooltipTextChecked="Disable"
        />
      </Box>
    </Box>
  );
}
